import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Loader } from "semantic-ui-react";
import BootFromVolumeFlavors from "../servers/create/boot-target/BootFromVolumeFlavors";
import FetchAPI from "../../api/FetchAPI";
import { Flavor, FlavorsProps } from "./types";
import { getCurrentProjectID } from "../../app_shared_functions";
import { State } from "../../selectors/state";

const Flavors: React.FC<FlavorsProps> = ({
  region,
  handleChange,
  inputKey,
  isRequired,
  value,
  disabled,
  minRam,
  maxRam,
  minCores,
  maxCores,
  flavorTypes,
  errorMessage,
}) => {
  const [flavors, setFlavors] = useState<Flavor[] | "loading">("loading");
  const projects = useSelector((state: State) => state.projects);

  const project_id = getCurrentProjectID(projects, region);

  useEffect(() => {
    if (project_id) {
      const fetchFlavors = async () => {
        try {
          const response = await FetchAPI.Compute.Flavors.getList({
            region,
            project_id,
          });

          const flavorOptions: Flavor[] = response.data
            .filter((flavor: any) => {
              const isRamValid =
                (minRam === undefined || flavor.ram >= minRam) &&
                (maxRam === undefined || flavor.ram <= maxRam);

              const isCoresValid =
                (minCores === undefined || flavor.vcpus >= minCores) &&
                (maxCores === undefined || flavor.vcpus <= maxCores);

              const isFlavorTypeValid =
                !Array.isArray(flavorTypes) ||
                flavorTypes.length === 0 ||
                flavorTypes.some((type) => flavor.profiles.includes(type));

              return isRamValid && isCoresValid && isFlavorTypeValid;
            })
            .map((flavor: any) => ({
              id: flavor.id,
              value: flavor.id,
              text: `${flavor.name} (${flavor.ram / 1024} GB RAM, ${
                flavor.vcpus
              } vCPUs)`,
              ram: flavor.ram,
              cores: flavor.vcpus,
            }));

          flavorOptions.sort((a, b) => {
            if (a.cores !== b.cores) {
              return a.cores - b.cores;
            }
            return a.ram - b.ram;
          });
          setFlavors(flavorOptions);
        } catch (error) {
          setFlavors([]);
          console.error("Failed to load flavors", error);
        }
      };

      fetchFlavors();
    } else {
      setFlavors([]);
    }
  }, [region, project_id, minRam, maxRam, minCores, maxCores, flavorTypes]);

  const onFlavorUpdate = useCallback(
    (flavor: Flavor) => {
      handleChange({
        name: inputKey,
        value: flavor,
      });
    },
    [handleChange, inputKey],
  );

  const memoizedFlavors = useMemo(() => {
    if (flavors === "loading") {
      return (
        <Loader size="mini" active className="one-liner">
          Fetching flavors...
        </Loader>
      );
    }

    return (
      <>
        <BootFromVolumeFlavors
          disabled={disabled}
          flavors={flavors}
          flavor={value}
          onFlavorUpdate={onFlavorUpdate}
          isRequired={isRequired}
        />
        {errorMessage && <span className="error-message">{errorMessage}</span>}
      </>
    );
  }, [flavors, disabled, value, onFlavorUpdate, isRequired, errorMessage]);

  return memoizedFlavors;
};

export default Flavors;
